import * as React from "react"
import { useRevokeInvitationMutation } from "@modules/organization/mutations.generated"
import {
  useShowSuccessToast,
  StyledModal,
  StyledModalHeader,
  StyledModalBody,
  StyledModalActions,
  Button,
  Text,
} from "gatsby-interface"
import { ModalMessage } from "@modules/modal"
import {
  manageContributors as text,
  ui as uiText,
} from "@modules/locales/default.js"
import { useTracker, SegmentEventType } from "@modules/analytics"
import { commonMessageTags, FormattedMessage } from "@modules/locales"
import { useTriggerErrorAlert } from "@modules/ui/components/ErrorAlert"
import { StyledForm } from "@modules/ui/components/Primitives"
import { WorkspaceInvitationsDocument } from "@modules/organization/contributors/queries.generated"

export type RevokeMemberInviteModalProps = {
  closeModal: () => void
  email: string
  contributorId: string
  resourceId: string
  organizationId: string
  recalculateUsage?: () => void
}

function RevokeMemberInviteModal({
  closeModal,
  email,
  contributorId,
  resourceId,
  recalculateUsage,
}: RevokeMemberInviteModalProps) {
  const { trackSegment } = useTracker()
  const showSuccessToast = useShowSuccessToast()
  const [mutate, { loading }] = useRevokeInvitationMutation()
  const [setError, errorAlert] = useTriggerErrorAlert()

  return (
    <StyledModal variant="WARNING">
      <StyledModalHeader onCloseButtonClick={closeModal}>
        {text.headers.revokeInviteQuestion}
      </StyledModalHeader>
      <StyledModalBody>
        <ModalMessage>
          <Text>
            <FormattedMessage<"email", "strong">
              message={text.messages.revokeOrgInviteConf}
              values={{ email }}
              tags={commonMessageTags}
            />
          </Text>
          {errorAlert}
        </ModalMessage>
        <StyledForm
          onSubmit={e => {
            e.preventDefault()
            trackSegment({
              type: SegmentEventType.Track,
              event: `Revoked a member invite`,
              properties: {
                location: `Members tab - "Revoke invite" modal`,
              },
            })
            mutate({
              variables: {
                id: contributorId,
              },
              refetchQueries: [
                {
                  query: WorkspaceInvitationsDocument,
                  variables: {
                    workspaceId: resourceId,
                  },
                },
              ],
            })
              .then(() => {
                closeModal()
                showSuccessToast(text.messages.memberInvitationRevoked)
                recalculateUsage && recalculateUsage()
              })
              .catch(err => {
                setError(err)
              })
          }}
        >
          <StyledModalActions>
            <Button
              variant="SECONDARY"
              tone="NEUTRAL"
              type="button"
              onClick={() => {
                trackSegment({
                  type: SegmentEventType.Track,
                  event: `Cancelled revoking member invite`,
                  properties: {
                    location: `Members tab - "Revoke invite" modal`,
                  },
                })
                closeModal()
              }}
            >
              {uiText.actions.cancel}
            </Button>
            <Button
              loading={loading}
              variant="PRIMARY"
              tone="DANGER"
              type="submit"
              data-cy="revoke-invite-cta"
            >
              {text.actions.revokeInvite}
            </Button>
          </StyledModalActions>
        </StyledForm>
      </StyledModalBody>
    </StyledModal>
  )
}

export default RevokeMemberInviteModal
